import {
  platformApiRequest,
  platformApiBlob,
  superAdminTokenStore,
} from '../lib/platform-auth.js';
import type { EntitlementKey, EntitlementValue, EmployerBankAccount } from '@kora/types';
import type {
  SubscriptionMe,
  SubscriptionStatus,
  SubscriptionTier,
} from './subscriptions.js';

// Re-export the canonical subscription enums so the shared StatusBadge and the
// platform mutation forms can import them from the operator module.
export type { SubscriptionStatus, SubscriptionTier } from './subscriptions.js';

/**
 * Operator-facing subscription view — the superset returned by the C-1 detail
 * endpoint's `subscription` block and by every C-2 mutation response. Adds the
 * operator/founding-internal fields the tenant view omits.
 *
 * NB: the tenant-only `/me` fields (effectiveEntitlements, employeeCount,
 * employeeCap — added in D-3/D-4) are Omit'd — the operator subscription
 * responses don't carry them (the operator surfaces entitlements via the
 * dedicated entitlements endpoints and the Headcount cap fields instead).
 */
export interface SubscriptionDetail
  // billingChannel/openInvoice are computed in the tenant /me ROUTE (they need
  // invoice queries), not in formatTenantSubscription — so the operator view,
  // which spreads the formatter, does not carry them.
  extends Omit<SubscriptionMe, 'effectiveEntitlements' | 'employeeCount' | 'employeeCap' | 'billingChannel' | 'openInvoice'> {
  id: string;
  lockedRate: string | null;
  lockEndsAt: string | null;
  cancellationReason: string | null;
  billingEmail: string | null;
  // Operator-only dunning field (the tenant /me view omits this).
  dunningExempt: boolean;
}

/** A dunning-related SubscriptionEvent row (operator history). */
export interface DunningEvent {
  id: string;
  action: string;
  fromStatus: SubscriptionStatus | null;
  toStatus: SubscriptionStatus | null;
  actorType: string;
  actorId: string | null;
  metadata: unknown;
  createdAt: string;
}

/** POST /v1/platform/tenants/:id/subscription/transition */
export function transitionTenantSubscription(
  tenantId: string,
  body: { toStatus: 'active' | 'past_due' | 'read_only'; reason: string; advanceClockTo?: string },
): Promise<{ subscription: SubscriptionDetail; noop: boolean }> {
  return platformApiRequest(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/transition`, {
    method: 'POST',
    body,
  });
}

/** PUT /v1/platform/tenants/:id/subscription/dunning-exempt */
export function setTenantDunningExempt(
  tenantId: string,
  body: { exempt: boolean; reason: string },
): Promise<SubscriptionDetail> {
  return platformApiRequest(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/dunning-exempt`, {
    method: 'PUT',
    body,
  });
}

/** GET /v1/platform/tenants/:id/subscription/dunning-history */
export function getDunningHistory(
  tenantId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<{ events: DunningEvent[]; total: number }> {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return platformApiRequest(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/dunning-history${q ? `?${q}` : ''}`,
  );
}

/**
 * Typed calls for the operator console, consuming the Phase A auth endpoints
 * and the Phase B-1 tenant read endpoints. Mirrors the shapes in
 * apps/api/src/routes/platform/{auth,tenants}.ts — keep in sync with those.
 *
 * All requests go through platformApiRequest (the parallel super-admin
 * client), never the tenant api/client.ts.
 */

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

interface PlatformLoginResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  requiresSetup: boolean;
  requires2fa: boolean;
}

export interface PlatformLoginResult {
  requiresSetup: boolean;
  requires2fa: boolean;
}

/** POST /v1/platform/auth/login — stores the partial (pre-2FA) access token. */
export async function platformLogin(input: {
  email: string;
  password: string;
}): Promise<PlatformLoginResult> {
  const res = await platformApiRequest<PlatformLoginResponse>('/v1/platform/auth/login', {
    method: 'POST',
    body: input,
    skipRefresh: true, // 401 here = bad credentials, not an expired token
  });
  superAdminTokenStore.set(res.accessToken);
  return { requiresSetup: res.requiresSetup, requires2fa: res.requires2fa };
}

export interface Platform2faSetupResult {
  qrCodeDataUri: string;
  manualEntryKey: string;
}

/** POST /v1/platform/auth/2fa/setup — first-time TOTP enrollment (partial token). */
export function platform2faSetup(): Promise<Platform2faSetupResult> {
  return platformApiRequest<Platform2faSetupResult>('/v1/platform/auth/2fa/setup', {
    method: 'POST',
    body: {},
    skipRefresh: true,
  });
}

interface Platform2faVerifyResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
}

/** POST /v1/platform/auth/2fa/verify — completes 2FA; stores the full token. */
export async function platform2faVerify(input: { code: string }): Promise<void> {
  const res = await platformApiRequest<Platform2faVerifyResponse>(
    '/v1/platform/auth/2fa/verify',
    { method: 'POST', body: input, skipRefresh: true },
  );
  superAdminTokenStore.set(res.accessToken);
}

// ---------------------------------------------------------------------------
// Tenants (Phase B-1 read endpoints)
// ---------------------------------------------------------------------------

export interface TenantListRow {
  id: string;
  name: string;
  countryCode: string;
  // Phase C-2 amendment: `status`/`tier` reflect the tenant's Subscription
  // (null only for a pre-C-2 orphan org with no subscription row).
  status: SubscriptionStatus | null;
  tier: SubscriptionTier | null;
  createdAt: string;
  employeeCount: number;
}

export interface TenantListResponse {
  tenants: TenantListRow[];
  nextCursor: string | null;
}

export interface ListTenantsParams {
  cursor?: string;
  limit?: number;
  status?: SubscriptionStatus;
  search?: string;
  sortBy?: 'name' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

/** GET /v1/platform/tenants — cursor-paginated, filterable, sortable. */
export function listTenants(params: ListTenantsParams = {}): Promise<TenantListResponse> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.status) qs.set('status', params.status);
  if (params.search) qs.set('search', params.search);
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  if (params.sortOrder) qs.set('sortOrder', params.sortOrder);
  const query = qs.toString();
  return platformApiRequest<TenantListResponse>(
    `/v1/platform/tenants${query ? `?${query}` : ''}`,
  );
}

export interface TenantDetail {
  id: string;
  name: string;
  countryCode: string;
  // Top-level `status` reflects Subscription.status (Phase C-2). Null only for
  // a pre-C-2 orphan org. Full subscription state lives in `subscription`.
  status: SubscriptionStatus | null;
  createdAt: string;
  updatedAt: string;
  employeeCount: number;
  activeEmployeeCount: number;
  // Phase D-4 cap visibility: seats used = active + on_leave (cap-counted);
  // employeeCap = resolved max_employees (null = unlimited); capIsOverride =
  // the cap differs from the tier default.
  seatsUsed: number;
  employeeCap: number | null;
  capIsOverride: boolean;
  userCount: number;
  payrollRunCount: number;
  lastPayrollRunDate: string | null;
  subscription: SubscriptionDetail | null;
  complianceConfig: { configured: boolean; countries: string[] };
  // Read-only operator view of the tenant's declared bank accounts
  // (bank-export feature). Null when the tenant hasn't configured a profile.
  bankProfile: {
    accounts: EmployerBankAccount[];
    narrationTemplate: string | null;
  } | null;
  // Loan portfolio composition over in-flight loans (loan-migration feature).
  loanPortfolio: {
    activeLoanCount: number;
    originationBreakdown: { in_app_request: number; hr_direct_create: number; migration: number };
    totalOutstandingBalance: Record<string, string>;
  };
}

/** GET /v1/platform/tenants/:id — full read-only detail (incl. subscription). */
export function getTenant(id: string): Promise<TenantDetail> {
  return platformApiRequest<TenantDetail>(`/v1/platform/tenants/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// Subscription mutations (Phase C-2 endpoints; consumed by the C-3 modals)
// ---------------------------------------------------------------------------
// Each is gated server-side by requireSuperAdmin + withSuperAdminAudit and
// returns the updated operator-facing Subscription. They throw PlatformApiError
// on non-2xx (the body carries C-2's structured 400/409 — the modals surface
// `.message` to the operator).

/** POST /v1/platform/tenants/:id/suspend */
export function suspendTenant(
  tenantId: string,
  body: { reason: string },
): Promise<SubscriptionDetail> {
  return platformApiRequest<SubscriptionDetail>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/suspend`,
    { method: 'POST', body },
  );
}

/** POST /v1/platform/tenants/:id/restore */
export function restoreTenant(
  tenantId: string,
  body: { reason: string },
): Promise<SubscriptionDetail> {
  return platformApiRequest<SubscriptionDetail>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/restore`,
    { method: 'POST', body },
  );
}

/**
 * POST /v1/platform/tenants/:id/cancel
 *
 * `detachProvider: true` is the ORPHANED-REF escape hatch: cancel locally
 * WITHOUT calling Paddle, for a subscription whose provider ref Paddle no
 * longer knows. The server verifies the ref is genuinely not-found before
 * allowing it (a live ref is refused with 409 PROVIDER_REF_IS_LIVE), so this
 * flag cannot be used to force a still-billing subscription cancelled.
 */
export function cancelTenant(
  tenantId: string,
  body: { reason: string; detachProvider?: boolean },
): Promise<SubscriptionDetail> {
  return platformApiRequest<SubscriptionDetail>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/cancel`,
    { method: 'POST', body },
  );
}

/** POST /v1/platform/tenants/:id/subscription/change-tier */
export function changeTenantTier(
  tenantId: string,
  body: { toTier: SubscriptionTier; reason: string },
): Promise<SubscriptionDetail> {
  return platformApiRequest<SubscriptionDetail>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/change-tier`,
    { method: 'POST', body },
  );
}

/** POST /v1/platform/tenants/:id/subscription/set-founding */
export function setTenantFounding(
  tenantId: string,
  body: {
    isFoundingCustomer: boolean;
    lockedRate?: string;
    lockEndsAt?: string;
    reason: string;
  },
): Promise<SubscriptionDetail> {
  return platformApiRequest<SubscriptionDetail>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/set-founding`,
    { method: 'POST', body },
  );
}

/** POST /v1/platform/tenants/:id/subscription/activate (Phase D-1) */
export function activateTenant(
  tenantId: string,
  body: { reason: string; billingPeriodMonths?: number; invoiceId?: string },
): Promise<SubscriptionDetail> {
  return platformApiRequest<SubscriptionDetail>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/activate`,
    { method: 'POST', body },
  );
}

/**
 * POST /v1/platform/tenants/:id/subscription/initialize (PR-2a follow-up).
 * Sets the first billing window for a pre-commercial sub with NULL period
 * dates. 422 SUBSCRIPTION_ALREADY_INITIALIZED if already initialized.
 */
export function initializeTenantSubscription(
  tenantId: string,
  body: { periodStart: string; periodEnd: string; invoiceId?: string; reason: string },
): Promise<SubscriptionDetail> {
  return platformApiRequest<SubscriptionDetail>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/initialize`,
    { method: 'POST', body },
  );
}

// ---------------------------------------------------------------------------
// Invoices (Phase D-2 operator endpoints)
// ---------------------------------------------------------------------------
// Mirrors apps/api/src/routes/platform/invoices.ts. Mutations are gated by
// requireSuperAdmin + withSuperAdminAudit and return the operator-facing
// Invoice. They throw PlatformApiError on non-2xx (the body carries the
// structured 400/404/409 — the modals surface `.message`).

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue' | 'voided';

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitAmount: number;
  totalAmount: number;
}

/** Operator-facing invoice — superset returned by every operator endpoint. */
export interface InvoiceDetail {
  id: string;
  number: string;
  status: InvoiceStatus;
  currency: string;
  subtotalAmount: string;
  taxAmount: string;
  totalAmount: string;
  lineItems: InvoiceLineItem[];
  notes: string | null;
  /** ADR-0032 D2 — the period this invoice pays for (both or neither). */
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  dueDate: string;
  issuedAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  createdAt: string;
  organizationId: string;
  subscriptionId: string;
  paymentReference: string | null;
  voidReason: string | null;
  createdBy: string;
  updatedAt: string;
}

export interface InvoiceListResponse {
  invoices: InvoiceDetail[];
  nextCursor: string | null;
}

export interface ListInvoicesParams {
  subscriptionId?: string;
  organizationId?: string;
  status?: InvoiceStatus;
  cursor?: string;
  limit?: number;
}

/** GET /v1/platform/invoices — cursor-paginated, filterable. */
export function listInvoices(params: ListInvoicesParams = {}): Promise<InvoiceListResponse> {
  const qs = new URLSearchParams();
  if (params.subscriptionId) qs.set('subscriptionId', params.subscriptionId);
  if (params.organizationId) qs.set('organizationId', params.organizationId);
  if (params.status) qs.set('status', params.status);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return platformApiRequest<InvoiceListResponse>(
    `/v1/platform/invoices${query ? `?${query}` : ''}`,
  );
}

/** GET /v1/platform/invoices/:id */
export function getInvoice(id: string): Promise<InvoiceDetail> {
  return platformApiRequest<InvoiceDetail>(`/v1/platform/invoices/${encodeURIComponent(id)}`);
}

/**
 * ADR-0032 — what paying an invoice does (or, from the preview, WILL do) to the
 * subscription. Server-derived by the same planner mark-paid runs: render it,
 * never re-derive it here.
 */
export interface CoverageOutcome {
  result: 'extended' | 'unchanged_already_covered' | 'skipped_status' | 'no_service_period';
  statusChange: { from: string; to: string } | null;
  fromPeriodEnd: string | null;
  toPeriodEnd: string | null;
  warning: string | null;
}

/** GET /v1/platform/invoices/:id/mark-paid-preview — read-only. */
export function getMarkPaidPreview(id: string): Promise<{ coverage: CoverageOutcome | null }> {
  return platformApiRequest(`/v1/platform/invoices/${encodeURIComponent(id)}/mark-paid-preview`);
}

/** POST /v1/platform/invoices — create a draft invoice. */
export function createInvoice(body: {
  subscriptionId: string;
  currency: string;
  dueDate: string;
  lineItems: InvoiceLineItem[];
  taxAmount?: number;
  notes?: string;
  servicePeriodStart?: string | null;
  servicePeriodEnd?: string | null;
  reason: string;
}): Promise<InvoiceDetail> {
  return platformApiRequest<InvoiceDetail>('/v1/platform/invoices', { method: 'POST', body });
}

/**
 * PATCH /v1/platform/invoices/:id — edit a DRAFT (2026-09-13). Replaces the
 * editable fields; totals are re-derived server-side. 409 NOT_DRAFT once issued.
 */
export function updateDraftInvoice(
  id: string,
  body: {
    dueDate: string;
    lineItems: InvoiceLineItem[];
    taxAmount?: number;
    notes?: string | null;
    servicePeriodStart?: string | null;
    servicePeriodEnd?: string | null;
    reason: string;
  },
): Promise<InvoiceDetail> {
  return platformApiRequest<InvoiceDetail>(`/v1/platform/invoices/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}

/** POST /v1/platform/invoices/:id/issue — draft → issued. */
export function issueInvoice(id: string, body: { reason: string }): Promise<InvoiceDetail> {
  return platformApiRequest<InvoiceDetail>(
    `/v1/platform/invoices/${encodeURIComponent(id)}/issue`,
    { method: 'POST', body },
  );
}

/** POST /v1/platform/invoices/:id/mark-paid — issued|overdue → paid. */
export function markInvoicePaid(
  id: string,
  body: { reason: string; paymentReference: string; paidAt?: string },
): Promise<InvoiceDetail & { coverage: CoverageOutcome | null }> {
  return platformApiRequest<InvoiceDetail & { coverage: CoverageOutcome | null }>(
    `/v1/platform/invoices/${encodeURIComponent(id)}/mark-paid`,
    { method: 'POST', body },
  );
}

/** POST /v1/platform/invoices/:id/void — any non-voided → voided. */
export function voidInvoice(
  id: string,
  body: { reason: string; voidReason: string },
): Promise<InvoiceDetail & { coverageWarning: string | null }> {
  return platformApiRequest<InvoiceDetail & { coverageWarning: string | null }>(
    `/v1/platform/invoices/${encodeURIComponent(id)}/void`,
    { method: 'POST', body },
  );
}

/**
 * Fetch the invoice PDF (bearer-authenticated) and open it in a new tab via an
 * object URL. The platform routes are bearer-gated, so a plain window.open of
 * the URL would 401 — we fetch the blob with the token, then open it.
 */
export async function openInvoicePdf(id: string): Promise<void> {
  const blob = await platformApiBlob(`/v1/platform/invoices/${encodeURIComponent(id)}/pdf`);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after a tick so the new tab has time to load the resource.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ---------------------------------------------------------------------------
// Entitlements (Phase D-3 operator endpoints)
// ---------------------------------------------------------------------------
// Mirrors the entitlement endpoints in apps/api/src/routes/platform/
// subscriptions.ts. override/reset are gated by requireSuperAdmin +
// withSuperAdminAudit and return the operator view; they throw PlatformApiError
// on non-2xx (the modals surface `.message`).

/** Operator view returned by the entitlement endpoints (get/override/reset). */
export interface EntitlementView {
  organizationId: string;
  tier: SubscriptionTier;
  /** Only the keys where the tenant diverges from its tier default. */
  overrides: Partial<Record<EntitlementKey, EntitlementValue>>;
  tierDefaults: Record<EntitlementKey, EntitlementValue>;
  effectiveEntitlements: Record<EntitlementKey, EntitlementValue>;
  updatedAt: string | null;
}

/** GET /v1/platform/tenants/:id/entitlements */
export function getEntitlements(tenantId: string): Promise<EntitlementView> {
  return platformApiRequest<EntitlementView>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/entitlements`,
  );
}

/** POST /v1/platform/tenants/:id/subscription/override-entitlement */
export function overrideEntitlement(
  tenantId: string,
  body: { key: EntitlementKey; value: EntitlementValue; reason: string },
): Promise<EntitlementView> {
  return platformApiRequest<EntitlementView>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/override-entitlement`,
    { method: 'POST', body },
  );
}

/** POST /v1/platform/tenants/:id/subscription/reset-entitlements */
export function resetEntitlements(tenantId: string, reason: string): Promise<EntitlementView> {
  return platformApiRequest<EntitlementView>(
    `/v1/platform/tenants/${encodeURIComponent(tenantId)}/subscription/reset-entitlements`,
    { method: 'POST', body: { reason } },
  );
}

// ─── Paddle catalogue mappings (ADR-0026 PR-3a) ─────────────────────────────

/** A tier → Paddle product/price row (GET/PUT response `mapping`). */
export interface PaddleMapping {
  tier: SubscriptionTier;
  paddleProductId: string;
  paddlePriceId: string;
  paddleAnnualPriceId: string | null;
  updatedAt: string;
}

/** The fetched Paddle price the operator sees before committing. */
export interface PaddlePriceView {
  priceId: string;
  productId: string;
  productName: string | null;
  priceName: string | null;
  amount: string;
  currencyCode: string;
  interval: 'day' | 'week' | 'month' | 'year' | null;
  intervalFrequency: number | null;
  trial: { interval: string; frequency: number; requiresPaymentMethod: boolean | null } | null;
  status: string;
}

/** One slot's validation outcome (mirrors the backend). */
export interface SlotValidation {
  slot: 'monthly' | 'annual';
  priceId: string;
  ok: boolean;
  code?: 'PRICE_NOT_FOUND' | 'PRODUCT_MISMATCH' | 'INTERVAL_MISMATCH';
  message?: string;
  price: PaddlePriceView | null;
}

/** The candidate ids the operator is validating / saving. */
export interface PaddleMappingInput {
  paddleProductId: string;
  paddlePriceId: string;
  paddleAnnualPriceId?: string | null;
}

/** GET /v1/platform/paddle-mappings */
export function listPaddleMappings(): Promise<{ mappings: PaddleMapping[] }> {
  return platformApiRequest<{ mappings: PaddleMapping[] }>('/v1/platform/paddle-mappings');
}

/** POST /v1/platform/paddle-mappings/:tier/validate — preview, no persist. */
export function validatePaddleMapping(
  tier: SubscriptionTier,
  body: PaddleMappingInput,
): Promise<{ validations: SlotValidation[]; ok: boolean }> {
  return platformApiRequest(`/v1/platform/paddle-mappings/${encodeURIComponent(tier)}/validate`, {
    method: 'POST',
    body,
  });
}

/** PUT /v1/platform/paddle-mappings/:tier — re-validates then persists. */
export function upsertPaddleMapping(
  tier: SubscriptionTier,
  body: PaddleMappingInput & { reason: string },
): Promise<{ mapping: PaddleMapping; validations: SlotValidation[] }> {
  return platformApiRequest(`/v1/platform/paddle-mappings/${encodeURIComponent(tier)}`, {
    method: 'PUT',
    body,
  });
}
